import React, { useState, useRef, useCallback } from 'react';
import { arrayOf, func, node, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import { OutsideClickHandler } from '../../../components';
import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import PriceFilterForm from '../PriceFilterForm/PriceFilterForm';
import css from './PriceFilterPopup.module.css';

const KEY_CODE_ESCAPE = 27;
const RADIX = 10;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = priceRange => {
  const [minPrice, maxPrice] = priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;
  return { [queryParamName]: value };
};

function PriceFilterPopup({
  rootClassName = null,
  className = null,
  id,
  label,
  queryParamNames,
  onSubmit,
  initialValues = null,
  contentPlacementOffset = 0,
  min,
  max,
  step = 1,
  marketplaceCurrency,
  liveEdit: _liveEdit = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef(null);
  const filterContentRef = useRef(null);

  const handleSubmit = useCallback(
    values => {
      setIsOpen(false);
      const priceQueryParamName = getPriceQueryParamName(queryParamNames);
      onSubmit(format(values, priceQueryParamName));
    },
    [onSubmit, queryParamNames]
  );

  const handleClear = useCallback(() => {
    setIsOpen(false);
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(null, priceQueryParamName));
  }, [onSubmit, queryParamNames]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    onSubmit(initialValues);
  }, [onSubmit, initialValues]);

  const handleBlur = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleKeyDown = useCallback(e => {
    if (e.keyCode === KEY_CODE_ESCAPE) {
      setIsOpen(false);
    }
  }, []);

  const toggleOpen = useCallback(enforcedState => {
    if (typeof enforcedState === 'boolean') {
      setIsOpen(enforcedState);
    } else {
      setIsOpen(prev => !prev);
    }
  }, []);

  const positionStyleForContent = useCallback(() => {
    const filter = filterRef.current;
    const filterContent = filterContentRef.current;
    if (filter && filterContent) {
      const distanceToRight = window.innerWidth - filter.getBoundingClientRect().right;
      const labelWidth = filter.offsetWidth;
      const contentWidth = filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;
      return { ...offset, ...minWidth };
    }
    return {};
  }, [contentPlacementOffset]);

  const classes = classNames(rootClassName || css.root, className);
  const priceQueryParam = getPriceQueryParamName(queryParamNames);
  const initialPrice =
    initialValues && initialValues[priceQueryParam] ? parse(initialValues[priceQueryParam]) : {};
  const { minPrice, maxPrice } = initialPrice || {};
  const hasValue = value => value != null;
  const hasInitialValues = initialValues && hasValue(minPrice) && hasValue(maxPrice);

  // Inline i18n: label for selected and default
  const currentLabel = hasInitialValues
    ? `Price: ${formatCurrencyMajorUnit(
        null,
        marketplaceCurrency,
        minPrice
      )} - ${formatCurrencyMajorUnit(null, marketplaceCurrency, maxPrice)}`
    : label || 'Price';

  const contentStyle = positionStyleForContent();

  return (
    <OutsideClickHandler onOutsideClick={handleBlur}>
      <div className={classes} onKeyDown={handleKeyDown} ref={filterRef}>
        <PopupOpenerButton isSelected={hasInitialValues} toggleOpen={toggleOpen}>
          {currentLabel}
        </PopupOpenerButton>
        <PriceFilterForm
          id={id}
          initialValues={hasInitialValues ? initialPrice : { minPrice: min, maxPrice: max }}
          onClear={handleClear}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          contentRef={filterContentRef}
          style={contentStyle}
          min={min}
          max={max}
          step={step}
          showAsPopup
          isOpen={isOpen}
        />
      </div>
    </OutsideClickHandler>
  );
}



PriceFilterPopup.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  initialValues: shape({
    price: string,
  }),
  contentPlacementOffset: number,
  min: number.isRequired,
  max: number.isRequired,
  step: number,
  marketplaceCurrency: string.isRequired,
};

export default PriceFilterPopup;
